import { useMemo, useState } from 'react';

import useEvent from '@lyearn/core/hooks/useEvent';
import useToggle from '@lyearn/core/hooks/useToggle';
import { FilterGroup, FilterOption } from '@/components/Filter/types';
import useRelatedSkills from '@/modules/corporate/modules/feedback/hooks/useRelatedSkills';
import {
  getSkillFilterOption,
  SkillMenuItem,
} from '@/modules/corporate/modules/feedback/views/FeedbackWall/components/CreateFeedbackModal/components/RelatedSkillsOrMindsets/utils';

const useSkillsFilter = ({
  onChange,
  skillIds,
  isMindset,
}: {
  onChange: (value: string[]) => void;
  skillIds: string[];
  isMindset?: boolean;
}) => {
  const [selectedSkillsState, setSelectedSkills] = useState<SkillMenuItem[]>();
  const [open, { toggle }] = useToggle(false);
  const { skills, fetching, searchText, setSearchText } = useRelatedSkills({
    skillIds,
    isMindset,
    pause: true,
  });
  const SkillParam = isMindset ? 'Mindset' : 'Skill';

  const addSkill = useEvent((value: FilterOption[]) => {
    setSelectedSkills(value as SkillMenuItem[]);
    const newSkillIds = value.map((newSkill) => newSkill.value);
    onChange(newSkillIds);
  });

  const onRemoveSkill = useEvent((id: string) => {
    const newSkillIds = skillIds.filter((skillId) => id !== skillId);
    const newSelectedSkillsState = selectedSkillsState?.filter((skill) => id !== skill.id);
    onChange(newSkillIds);
    setSelectedSkills(newSelectedSkillsState);
  });

  const filterGroups: FilterGroup[] = useMemo(() => {
    return skills?.length
      ? [
          {
            id: 'skills',
            options: skills.map((skill) => getSkillFilterOption(skill)),
          },
        ]
      : [];
  }, [skills]);

  return {
    filterGroups,
    onRemoveSkill,
    addSkill,
    SkillParam,
    open,
    toggle,
    fetching,
    searchText,
    setSearchText,
    selectedSkillsState,
  };
};

export default useSkillsFilter;
